import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateShort } from "@/lib/formatDate";
import DownloadXlsxButton from "@/components/DownloadXlsxButton";
import DownloadPdfButton from "@/components/DownloadPdfButton";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Assigned inventory month (YYYY-MM); legacy counts without one fall back to their count date.
const monthOf = (c) => c.inventory_month || (c.count_date || "").slice(0, 7);

export default function InventoryExportPanel({ counts, scopeParams }) {
    const { t } = useTranslation();
    const latest = useMemo(() => counts.map(monthOf).filter(Boolean).sort().pop(), [counts]);
    const now = new Date();
    const [year, setYear] = useState(Number((latest || "").slice(0, 4)) || now.getFullYear());
    const [month, setMonth] = useState(Number((latest || "").slice(5, 7)) || now.getMonth() + 1);
    const [countId, setCountId] = useState(null);
    const key = `${year}-${String(month).padStart(2, "0")}`;
    const matches = useMemo(() => counts.filter((c) => monthOf(c) === key), [counts, key]);
    const years = useMemo(() => {
        const ys = new Set(counts.map((c) => Number(monthOf(c).slice(0, 4))).filter(Boolean));
        ys.add(now.getFullYear());
        return [...ys].sort((a, b) => b - a);
    }, [counts]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setCountId(matches.length === 1 ? matches[0].id : null); }, [matches]);
    const monthLabel = `${t(`months.${month}`)} ${year}`;

    return (
        <section className="kop-card p-5 mb-8" data-testid="inventory-export-panel">
            <h2 className="font-display text-lg font-bold">{t("inventory.exportTitle", "Download inventory report")}</h2>
            <div className="mt-3 flex flex-wrap items-end gap-3">
                <PeriodSelect testId="inventory-export-month" label={t("inventory.inventoryMonth", "Inventory Month")}
                    value={month} onChange={setMonth}
                    options={Array.from({ length: 12 }, (_, i) => [i + 1, t(`months.${i + 1}`)])} />
                <PeriodSelect testId="inventory-export-year" label={t("inventory.exportYear", "Year")}
                    value={year} onChange={setYear} options={years.map((y) => [y, String(y)])} />
                {countId && (
                    <div className="flex gap-2" data-testid="inventory-export-buttons">
                        <DownloadXlsxButton endpoint="/reports/inventory/xlsx" params={{ ...scopeParams, count_id: countId }}
                            filename={`Inventaarioraportti_${key}.xlsx`}
                            label={t("inventory.exportExcel", "Download Excel")} />
                        <DownloadPdfButton endpoint={`/inventory-counts/${countId}/pdf`} filename={`Inventaarioraportti_${key}.pdf`}
                            label={t("inventory.exportPdf", "Download PDF")} />
                    </div>
                )}
            </div>
            {matches.length === 0 && (
                <p className="mt-3 text-sm text-amber-800" data-testid="inventory-export-none">
                    {t("inventory.exportNone", { month: monthLabel, defaultValue: "No saved inventory for {{month}}. Choose another month." })}
                </p>
            )}
            {matches.length > 1 && (
                <CountChoice matches={matches} countId={countId} setCountId={setCountId} />
            )}
        </section>
    );
}

function PeriodSelect({ testId, label, value, onChange, options }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{label}</label>
            <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
                <SelectTrigger data-testid={testId} className="mt-1 h-10 w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                    {options.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
    );
}

function CountChoice({ matches, countId, setCountId }) {
    const { t } = useTranslation();
    return (
        <div className="mt-3" data-testid="inventory-export-choice">
            <p className="text-sm text-neutral-700">{t("inventory.exportChooseCount", "Several inventories are saved for this month. Choose one:")}</p>
            <div className="mt-2 flex flex-wrap gap-2">
                {matches.map((c) => (
                    <button key={c.id} type="button" aria-pressed={countId === c.id}
                        data-testid={`inventory-export-count-${c.id}`} onClick={() => setCountId(c.id)}
                        className={`h-9 px-3 rounded-md border text-sm ${countId === c.id
                            ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-300 hover:bg-neutral-50"}`}>
                        {formatDateShort(c.count_date)} · {c.completed_by || "—"}
                    </button>
                ))}
            </div>
        </div>
    );
}
